import { Text, Tooltip } from '@mantine/core'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { SDRStatus } from '@seen/core/models/tasks/sdr/utils'
import { now } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconArrowRight } from '@tabler/icons-react'
import type { JSX } from 'react'
import { SDRDisplayForwardBackButtons } from '#src/components/sdr/display/SDRDisplayForwardBackButtons.tsx'
import { SDRTimeRemainingWithTooltip } from '#src/components/sdr/display/SDRTimeRemainingWithTooltip.tsx'
import type { SDRDisplayStep } from '#src/components/sdr/display/utils.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

type Props = {
  sdr: SDR
  selectedStep: SDRDisplayStep
  setSelectedStep: (step: SDRDisplayStep) => void
}

export function SDRMarkNotified({
  sdr,
  selectedStep,
  setSelectedStep,
}: Props): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const sdrStatus = sdr.status
  const verbalCommunications = sdr.verbalNotificationAttempts
  const writtenCommunications = sdr.writtenNotificationAttempts

  let advanceDisabledMessage: string | null = null
  if (
    sdrStatus === SDRStatus.DENIED ||
    sdrStatus === SDRStatus.PARTIALLY_DENIED
  ) {
    advanceDisabledMessage =
      writtenCommunications.length > 0 && verbalCommunications.length > 0
        ? null
        : "Can't advance until both written and verbal communications have been sent"
  }

  const advanceMutation = usePatchMutation<SDR>()

  const notificationDate = sdr.notificationDate

  return (
    <div className="flex flex-row items-center justify-between">
      {notificationDate == null ? (
        <>
          <SDRTimeRemainingWithTooltip sdr={sdr} />
          {advanceMutation.error && (
            <Text className="text-red-600">
              {advanceMutation.error instanceof Error
                ? advanceMutation.error.message
                : String(advanceMutation.error)}
            </Text>
          )}
          <div>
            <Tooltip
              disabled={advanceDisabledMessage == null}
              label={advanceDisabledMessage}
            >
              <Button
                onClick={() => {
                  advanceMutation.mutate({
                    instance: sdr,
                    mutateFn: (sdr) => {
                      sdr.notificationDate = now()
                    },
                  })
                }}
                loading={advanceMutation.isPending}
                disabled={isReadOnly || advanceDisabledMessage != null}
              >
                Mark as Notified
                <IconArrowRight className="size-4" />
              </Button>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <div />
          <div>
            <SDRDisplayForwardBackButtons
              selectedStep={selectedStep}
              setSelectedStep={setSelectedStep}
            />
          </div>
        </>
      )}
    </div>
  )
}
